import { BusinessResponses } from '@/types'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { getFileUrl } from '@/clients/http'
import { Mail, Phone, MapPin, Image as ImageIcon } from 'lucide-react'
import { useState, useEffect } from 'react'
import { BusinessModel } from '@/http/models/business/Business'
import { toast } from 'sonner'
import { MediaGalleryModal } from '@/components/media/MediaGalleryModal'
import { useForm } from 'react-hook-form'
import { TextInput } from '@/components/atoms/inputs'
import { Spinner } from '@/components/ui/spinner'

interface GeneralSettingsProps {
    profile: BusinessResponses.BusinessProfile['result'] | null
    onRefresh: () => Promise<void>
}

export const GeneralSettings = ({ profile, onRefresh }: GeneralSettingsProps) => {
    const [isSaving, setIsSaving] = useState(false)
    const [galleryOpen, setGalleryOpen] = useState(false)
    const [galleryTarget, setGalleryTarget] = useState<'logo' | 'favicon' | null>(null)

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            name: '',
            email: '',
            contact_number: '',
            address: '',
            logo_id: '',
            logo_path: '',
            favicon_id: '',
            favicon_path: '',
        },
    })

    const formValues = watch()

    useEffect(() => {
        if (profile) {
            reset({
                name: profile.name || '',
                email: profile.email || '',
                contact_number: profile.contact_number || '',
                address: profile.address || '',
                logo_id: profile.logo_id || '',
                logo_path: profile.logo_path || '',
                favicon_id: profile.favicon_id || '',
                favicon_path: profile.favicon_path || '',
            })
        }
    }, [profile, reset])

    const onSubmit = async (data: any) => {
        setIsSaving(true)
        try {
            const response = await BusinessModel.updateBusinessProfile({
                body: data,
            })
            if (response.success) {
                toast.success('General settings updated')
                await onRefresh()
            } else {
                toast.error('Failed to update settings')
            }
        } catch (error) {
            toast.error('An error occurred')
        } finally {
            setIsSaving(false)
        }
    }

    const openGallery = (target: 'logo' | 'favicon') => {
        setGalleryTarget(target)
        setGalleryOpen(true)
    }

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader>
                    <CardTitle>Store details</CardTitle>
                    <CardDescription>Your store name and how customers can reach you.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <TextInput
                        control={control}
                        name="name"
                        label="Store name"
                        placeholder="Store name"
                        rules={{ required: 'Name is required' }}
                        errors={errors}
                    />
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <TextInput
                            control={control}
                            name="email"
                            label="Store email"
                            placeholder="email@example.com"
                            icon={<Mail className="size-4" />}
                            errors={errors}
                        />
                        <TextInput
                            control={control}
                            name="contact_number"
                            label="Phone"
                            placeholder="Phone number"
                            icon={<Phone className="size-4" />}
                            errors={errors}
                        />
                    </div>
                    <TextInput
                        control={control}
                        name="address"
                        label="Address"
                        placeholder="Store address"
                        icon={<MapPin className="size-4" />}
                        errors={errors}
                    />
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Logo and favicon</CardTitle>
                    <CardDescription>Shown on your storefront and in the browser tab.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-1.5">
                        <label className="block text-sm text-ink">Logo</label>
                        <div
                            className="group relative flex h-32 w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg border border-dashed border-border bg-subdued transition-colors hover:bg-fill"
                            onClick={() => openGallery('logo')}
                        >
                            {formValues.logo_path ? (
                                <img src={getFileUrl(formValues.logo_path)} alt="Logo" className="max-h-full max-w-full object-contain p-4" />
                            ) : (
                                <div className="space-y-2 text-center">
                                    <ImageIcon className="mx-auto size-6 text-muted-foreground" />
                                    <span className="text-sm text-text-2">Select logo</span>
                                </div>
                            )}
                            <div className="absolute inset-0 flex items-center justify-center bg-tooltip/40 opacity-0 transition-opacity group-hover:opacity-100">
                                <Button variant="secondary" size="sm">Change logo</Button>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-1.5 border-t border-border-subtle pt-4">
                        <label className="block text-sm text-ink">Favicon</label>
                        <div className="flex items-center gap-3">
                            <div
                                className="group relative flex size-16 cursor-pointer items-center justify-center overflow-hidden rounded-lg border border-dashed border-border bg-subdued transition-colors hover:bg-fill"
                                onClick={() => openGallery('favicon')}
                            >
                                {formValues.favicon_path ? (
                                    <img src={getFileUrl(formValues.favicon_path)} alt="Favicon" className="size-10 object-contain" />
                                ) : (
                                    <ImageIcon className="size-5 text-muted-foreground" />
                                )}
                            </div>
                            <p className="text-sm text-text-2">A small square image shown in the browser tab.</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <div className="flex justify-end">
                <Button onClick={handleSubmit(onSubmit)} disabled={isSaving} data-copilot-id="settings_save_button">
                    {isSaving && <Spinner />}
                    Save
                </Button>
            </div>

            <MediaGalleryModal
                open={galleryOpen}
                onOpenChange={setGalleryOpen}
                onSelect={(files) => {
                    if (files.length > 0 && galleryTarget) {
                        setValue(`${galleryTarget}_id`, files[0].id)
                        setValue(`${galleryTarget}_path`, files[0].url)
                    }
                }}
            />
        </div>
    )
}
